import type { InferType } from 'yup';
import { array, boolean, number, object, string } from 'yup';

export const CART_REQUEST_ORDER_KEY = 'cart-request';

const cartItemSchema = object().shape({
  productId: number().required(),
  skuId: number().required(),
  name: string().required(),
  code: string().nullable(),
  price: number().required(),
  basePrice: number().nullable(),
  isAvailable: boolean().required(),
  isGift: boolean().optional(),
  quantity: number().required(),
  // Маркеры акции «2+1»: winner — строка за 1 ₽ (бейдж «2+1»), group — «полная
  // в группе» (по полной цене, бейджа нет). optional — старый бэкенд без маркеров
  // не должен ронять валидацию корзины.
  twoPlusOneWinner: boolean().optional(),
  twoPlusOneGroup: boolean().optional(),
});

export const cartContentsResponseSchema = object().shape({
  items: array().ensure().of(cartItemSchema).required(),
  // true — бэкенд вычистил из корзины закончившиеся товары; по этому признаку
  // показываем плашку «Некоторые товары закончились. Корзина обновлена».
  itemsRemoved: boolean().optional().default(false),
});

export const cartRecommendationsResponseSchema = array().ensure().of(number().required()).required();

const updateCartItemBodySchema = object().shape({
  id: number().required(),
  quantity: number().required(),
});

const deleteCartItemBodySchema = object().shape({ id: number().required() });

const transferCartBodySchema = array().ensure().of(updateCartItemBodySchema).required();

export type CartItemType = InferType<typeof cartItemSchema>;

export const CartAPI = {
  contents: StrictFetch.prepare({
    url: 'basket',
    schemas: { response: cartContentsResponseSchema },
  }),
  updateItem: StrictFetch.prepare({
    method: HTTPMethod.PUT,
    url: 'basket',
    schemas: {
      response: cartContentsResponseSchema,
      body: updateCartItemBodySchema,
    },
    options: {
      orderKey: CART_REQUEST_ORDER_KEY,
      methodKey: ({ body }) => `order:updateItem:product-${body.id}`,
      selfInterrupted: true,
    },
  }),
  removeItem: StrictFetch.prepare({
    method: HTTPMethod.DELETE,
    url: 'basket',
    schemas: {
      response: cartContentsResponseSchema,
      body: deleteCartItemBodySchema,
    },
    options: { orderKey: CART_REQUEST_ORDER_KEY },
  }),
  transfer: StrictFetch.prepare({
    method: HTTPMethod.POST,
    url: 'basket/transfer',
    schemas: {
      response: cartContentsResponseSchema,
      body: transferCartBodySchema,
    },
  }),
  synchronize: StrictFetch.prepare({
    method: HTTPMethod.POST,
    url: 'basket/synchronize',
    schemas: {
      response: cartContentsResponseSchema,
      body: transferCartBodySchema,
    },
    options: {
      methodKey: 'basket:synchronize',
      selfInterrupted: true,
    },
  }),
  recommendations: StrictFetch.prepare({
    url: 'basket/recommendations',
    schemas: { response: cartRecommendationsResponseSchema },
    options: {
      methodKey: 'basket:recommendations',
      selfInterrupted: true,
    },
  }),
};
